import {
  SPACE_CHROME_COLOR_VARIABLE,
  SpaceBackdrop,
  SpaceSkyForegroundScope,
  useIsSpaceBackgroundEnabled,
} from "@-/dev.improve.haniverse.components.ui.space-backdrop";
import {
  type BottomTabBarButtonProps,
  type BottomTabBarProps,
  BottomTabBar,
} from "@-/com.npmjs.expo-router/bottom-tabs";
import { PlatformPressable } from "@-/com.npmjs.expo-router/react-navigation";
import {
  PRIMARY_NAVIGATION_STRIP_FADE_INSET_PX,
  getAndroidPrimaryNavigationBarBottomPadding,
  getPrimaryNavigationBarRowHeight,
  getPrimaryNavigationStripSurfaceClassName,
  usePrimaryNavigationStripFadeReachValue,
  usePrimaryNavigationStripSurfaceColorVariable,
} from "@-/dev.improve.haniverse.lib.primary-navigation-strip";
import { PRIMARY_NAVIGATION_STRIP_ICON_VERTICAL_INSET_PX } from "@-/dev.improve.haniverse.lib.primary-navigation-strip.entities";
import {
  IS_SYNC_STATUS_SENTINEL_PUBLISHED,
  SyncStatusSentinel,
} from "@-/dev.improve.haniverse.components.bundle-stamp";
import { Platform, Text, View } from "@-/com.npmjs.react-native";
import {
  PressedPrimaryNavigationDestinationStoreContext,
  PRIMARY_NAVIGATION_RAIL_WIDTH,
} from "@-/dev.improve.haniverse.routes.nav._context";
import { useCallback, useContext, useMemo } from "react";
import { ScrollEdgeFade } from "@-/dev.improve.haniverse.components.ui.scroll-edge-fade";
import {
  PrimaryNavigationStripEditor,
  useIsPrimaryNavigationStripEditing,
} from "@-/dev.improve.haniverse.routes.nav._lib.primary-navigation-customization";
import { orderPrimaryNavigationStripRoutes } from "#order-primary-navigation-strip-routes";

export type BalancedPrimaryNavigationStripButtonProps = BottomTabBarButtonProps & {
  bottomContentInset?: number;
  /**
   * Route name of the tab this button drives. Supplied only for real tab buttons, so the icon
   * can paint its pressed state; omitted for the bare `screenOptions` fallback button.
   */
  pressedTabName?: string;
  topContentInset?: number;
};

/**
 * Centers the tab icon-and-label stack vertically in the row across iOS and Android.
 */
const centerStyle = { justifyContent: "center" as const, paddingHorizontal: 0 };
const accessibilityLabelStyle = {
  bottom: 0,
  color: "rgba(0, 0, 0, 0.01)",
  fontSize: 1,
  includeFontPadding: false,
  left: 0,
  lineHeight: 1,
  position: "absolute" as const,
  right: 0,
  textAlign: "center" as const,
  textAlignVertical: "center" as const,
  top: 0,
};

export function BalancedPrimaryNavigationStripButton({
  bottomContentInset = 0,
  pressedTabName,
  topContentInset = 0,
  ...props
}: BalancedPrimaryNavigationStripButtonProps) {
  const {
    accessibilityLabel,
    accessibilityState,
    children,
    onPressIn,
    onPressOut,
    style,
    ...rest
  } = props;
  const pressableStyle = useMemo(
    () => [style, centerStyle, { paddingBottom: bottomContentInset, paddingTop: topContentInset }],
    [bottomContentInset, style, topContentInset],
  );

  // Press feedback starts on press-IN, so the icon reacts to the finger rather than to the
  // navigation that follows it — a tab whose scene is already mounted commits in a single frame,
  // which would otherwise leave the tap with no acknowledgement at all.
  const pressedTabStore = useContext(PressedPrimaryNavigationDestinationStoreContext);
  const handlePressIn = useCallback(
    (event: Parameters<NonNullable<typeof onPressIn>>[0]) => {
      if (pressedTabName !== undefined) {
        pressedTabStore?.press(pressedTabName);
      }
      onPressIn?.(event);
    },
    [onPressIn, pressedTabName, pressedTabStore],
  );
  const handlePressOut = useCallback(
    (event: Parameters<NonNullable<typeof onPressOut>>[0]) => {
      if (pressedTabName !== undefined) {
        pressedTabStore?.release(pressedTabName);
      }
      onPressOut?.(event);
    },
    [onPressOut, pressedTabName, pressedTabStore],
  );

  return (
    <PlatformPressable
      {...rest}
      accessibilityLabel={accessibilityLabel}
      accessibilityState={accessibilityState}
      accessible={false}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      style={pressableStyle}
    >
      {children}
      {accessibilityLabel === undefined ? null : (
        <Text
          accessibilityLabel={accessibilityLabel}
          accessibilityRole="button"
          accessibilityState={accessibilityState}
          accessible
          importantForAccessibility="yes"
          pointerEvents="none"
          style={accessibilityLabelStyle}
        >
          {accessibilityLabel}
        </Text>
      )}
    </PlatformPressable>
  );
}

export function PrimaryNavigationStrip({
  androidUsesButtonNavBar: androidUsesButtonNavBarProp,
  bottomClearance,
  destinationOrder,
  ...props
}: BottomTabBarProps & {
  /** When false (gesture nav), icons center in the full bar without bottom-only padding. */
  androidUsesButtonNavBar?: boolean;
  bottomClearance: number;
  /**
   * Route names in the order their buttons appear (the user's customized strip). Unlisted routes
   * follow in registration order; the navigator's own route order never changes.
   */
  destinationOrder?: readonly string[];
}) {
  // React Compiler cannot lower the platform comparison as a destructured default.
  // Resolve it here so the entire tab bar retains compiler memoization.
  const androidUsesButtonNavBar = androidUsesButtonNavBarProp ?? Platform.OS !== "android";
  const position =
    props.descriptors[props.state.routes[props.state.index].key].options.tabBarPosition;
  const isRail = position === "left" || position === "right";
  const isRightRail = position === "right";
  const hasDrawerAboveRail = isRightRail || props.state.routes[props.state.index].name === "learn";
  const fadeInset = PRIMARY_NAVIGATION_STRIP_FADE_INSET_PX;
  // How far the ramp climbs into the scene above the bar: 9pt unless the focused scene claims more.
  const fadeReach = usePrimaryNavigationStripFadeReachValue();
  // Accordion pages claim surface (white) so the fade matches section bodies; elsewhere muted.
  const surfaceColorVariable = usePrimaryNavigationStripSurfaceColorVariable();
  const surfaceBackgroundClassName =
    getPrimaryNavigationStripSurfaceClassName(surfaceColorVariable);
  // The experimental space background paints the sky on a muted bar: icons and the scene fade
  // follow it instead of the theme.
  const isSkySurface = useIsSpaceBackgroundEnabled() && surfaceBackgroundClassName === "bg-muted";
  const fadeColorVariable = isSkySurface ? SPACE_CHROME_COLOR_VARIABLE : surfaceColorVariable;
  // Zero out bottom inset so the underlying BottomTabBar doesn't add its own bottom padding
  // on top of our explicit height — that would push buttons up away from the bottom edge.
  // The `tabBarStyle.height` already accounts for the home-indicator / nav-bar clearance.
  const bottomTabBarInsets = useMemo(() => ({ ...props.insets, bottom: 0 }), [props.insets]);
  // Keep a minimum device-edge inset around the evenly spaced buttons.
  // Landscape side safe areas still protect the sensor housing.
  const horizontalContentInset = Math.max(9, props.insets.left, props.insets.right);
  // On Android with 3-button navigation, edge-to-edge overlays the nav bar on the tab bar;
  // bottom padding keeps icons above the buttons. Gesture navigation uses half clearance with
  // no bottom-only padding so spacing above/below icons stays symmetric.
  const tabBarStyle = useMemo(
    () =>
      isRail
        ? {
            backgroundColor: "transparent",
            borderLeftWidth: 0,
            borderRightWidth: 0,
            flex: 1,
            minWidth: 62,
            padding: 9,
            paddingBottom: 9 + bottomClearance,
            paddingEnd: 9,
            paddingStart: 9,
            // Learn keeps its full-height action rail clear by putting the drawer over either navigation rail.
            paddingTop: hasDrawerAboveRail
              ? getPrimaryNavigationBarRowHeight()
              : 9 + props.insets.top,
            width: PRIMARY_NAVIGATION_RAIL_WIDTH,
          }
        : {
            height: getPrimaryNavigationBarRowHeight() + bottomClearance,
            paddingHorizontal: horizontalContentInset,
            ...(fadeInset > 0 ? { backgroundColor: "transparent" } : {}),
            ...(Platform.OS === "android"
              ? {
                  paddingBottom: getAndroidPrimaryNavigationBarBottomPadding(
                    bottomClearance,
                    androidUsesButtonNavBar,
                  ),
                  ...(androidUsesButtonNavBar ? {} : { justifyContent: "center" as const }),
                }
              : {}),
          },
    [
      androidUsesButtonNavBar,
      bottomClearance,
      fadeInset,
      horizontalContentInset,
      isRail,
      hasDrawerAboveRail,
      props.insets.top,
    ],
  );

  // Keep global tabs icon-only regardless of upstream screenOptions. Merge the computed `tabBarStyle`
  // into options too; React Navigation only uses the custom `style` prop for height calculation,
  // while rendered padding comes from descriptor options.
  const bottomTabDescriptors = useMemo(() => {
    const next: typeof props.descriptors = {};
    // Descriptors are keyed by route KEY; the icons identify tabs by route NAME (the same name
    // the navigator's state and the tabPress listener use), so map one to the other here.
    const routeNameByKey: Record<string, string> = {};
    for (const route of props.state.routes) {
      routeNameByKey[route.key] = route.name;
    }
    const androidButtonNavItemStyle =
      !isRail && androidUsesButtonNavBar && Platform.OS === "android"
        ? ({
            alignSelf: "flex-start" as const,
            height: getPrimaryNavigationBarRowHeight(),
          } as const)
        : null;
    for (const key of Object.keys(props.descriptors)) {
      const descriptor = props.descriptors[key];
      const descriptorTabAccessibilityLabel =
        typeof descriptor.options.tabBarAccessibilityLabel === "string" &&
        descriptor.options.tabBarAccessibilityLabel.trim() !== ""
          ? descriptor.options.tabBarAccessibilityLabel
          : typeof descriptor.options.tabBarLabel === "string" &&
              descriptor.options.tabBarLabel.trim() !== ""
            ? descriptor.options.tabBarLabel
            : typeof descriptor.options.title === "string" && descriptor.options.title.trim() !== ""
              ? descriptor.options.title
              : undefined;
      next[key] = {
        ...descriptor,
        options: {
          ...descriptor.options,
          tabBarAccessibilityLabel:
            descriptorTabAccessibilityLabel ?? descriptor.options.tabBarAccessibilityLabel,
          // Drawer-only routes use a null tab-button renderer. Preserve it;
          // replacing it would mount invisible buttons, icons and labels on every switch.
          tabBarButton:
            descriptor.options.tabBarButtonTestID === undefined
              ? descriptor.options.tabBarButton
              : (buttonProps) => (
                  <BalancedPrimaryNavigationStripButton
                    {...buttonProps}
                    accessibilityLabel={
                      buttonProps.accessibilityLabel ?? descriptorTabAccessibilityLabel
                    }
                    bottomContentInset={
                      !isRail && Platform.OS === "ios"
                        ? bottomClearance + PRIMARY_NAVIGATION_STRIP_ICON_VERTICAL_INSET_PX
                        : 0
                    }
                    pressedTabName={routeNameByKey[key]}
                    style={isRail ? [buttonProps.style, { height: 44 }] : buttonProps.style}
                    topContentInset={
                      !isRail && Platform.OS === "ios"
                        ? PRIMARY_NAVIGATION_STRIP_ICON_VERTICAL_INSET_PX
                        : 0
                    }
                  />
                ),
          tabBarItemStyle: [
            descriptor.options.tabBarItemStyle,
            androidButtonNavItemStyle,
            // Equal auto margins implement justify-around inside React Navigation's row.
            // Apply the same layout at every tab count and orientation; allow shrinking
            // when all enabled buttons cannot fit at their preferred 44pt width.
            descriptor.options.tabBarButtonTestID !== undefined
              ? isRail
                ? {
                    flexBasis: 44,
                    flexGrow: 0,
                    flexShrink: 1,
                    marginVertical: "auto",
                    width: 44,
                  }
                : {
                    flexBasis: 44,
                    flexGrow: 0,
                    flexShrink: 1,
                    marginLeft: "auto",
                    marginRight: "auto",
                    width: 44,
                  }
              : null,
          ],
          tabBarShowLabel: false,
          ...(isRail ? { tabBarActiveBackgroundColor: "transparent" } : {}),
          tabBarStyle: [descriptor.options.tabBarStyle, tabBarStyle],
        },
      };
    }
    return next;
  }, [
    androidUsesButtonNavBar,
    bottomClearance,
    isRail,
    props.descriptors,
    props.state.routes,
    tabBarStyle,
  ]);

  const orderedState = useMemo(
    () => orderPrimaryNavigationStripRoutes(props.state, destinationOrder),
    [destinationOrder, props.state],
  );

  // While Settings → Primary Navigation is focused, the strip becomes its own editor in place: the
  // same box, with icons at the same centers as the buttons below would draw them.
  const isEditing = useIsPrimaryNavigationStripEditing();
  const rowHeight = getPrimaryNavigationBarRowHeight();
  const editor = isEditing ? (
    <PrimaryNavigationStripEditor
      crossCenter={
        isRail
          ? PRIMARY_NAVIGATION_RAIL_WIDTH / 2
          : // iOS buttons pad the bottom clearance out of their content box, Android's button-nav
            // items pin to the row's top, and Android gesture-nav items fill the whole bar.
            !androidUsesButtonNavBar && Platform.OS === "android"
            ? (rowHeight + bottomClearance) / 2
            : rowHeight / 2
      }
      edge={isRail ? (isRightRail ? "right" : "left") : "bottom"}
      leadingInset={
        isRail ? (hasDrawerAboveRail ? rowHeight : 9 + props.insets.top) : horizontalContentInset
      }
      style={tabBarStyle}
      trailingInset={isRail ? 9 + bottomClearance : horizontalContentInset}
    />
  ) : null;

  // The icons color themselves from theme tokens, so over the sky they take light ink.
  const bottomTabBar = (
    <SpaceSkyForegroundScope enabled={isSkySurface}>
      {editor ?? (
        <BottomTabBar
          {...props}
          descriptors={bottomTabDescriptors}
          insets={bottomTabBarInsets}
          state={orderedState}
          style={tabBarStyle}
        />
      )}
    </SpaceSkyForegroundScope>
  );

  if (isRail) {
    return (
      <View
        className={`z-10 w-[62px] ${surfaceBackgroundClassName}`}
        testID="app-primary-navigation-rail"
      >
        {isSkySurface ? <SpaceBackdrop /> : null}
        {bottomTabBar}
        {IS_SYNC_STATUS_SENTINEL_PUBLISHED ? <SyncStatusSentinel /> : null}
      </View>
    );
  }

  return (
    <View style={{ marginTop: -fadeInset }}>
      {/* Let the scene reach into the existing top padding without moving the tab row. */}
      {fadeInset > 0 ? (
        <View
          className={`absolute inset-x-0 bottom-0 ${surfaceBackgroundClassName}`}
          pointerEvents="none"
          style={{ top: fadeInset }}
        >
          {isSkySurface ? <SpaceBackdrop /> : null}
        </View>
      ) : null}
      {bottomTabBar}
      {/* Fade the live scene across the gap above the bar and its icon gutter. The ramp
          reaches the solid background before the icons; their opacity stays unchanged. */}
      <View
        className="absolute inset-x-0"
        pointerEvents="none"
        style={{ height: fadeReach + fadeInset, top: -fadeReach }}
      >
        <ScrollEdgeFade
          edge="bottom"
          height={fadeReach + fadeInset}
          surfaceColorVariable={fadeColorVariable}
        />
      </View>
      {/* The headless sync edge E2E flows block on (`actor-synced gen N`), in every build. */}
      {IS_SYNC_STATUS_SENTINEL_PUBLISHED ? <SyncStatusSentinel /> : null}
    </View>
  );
}
